/* 程昱（字仲德） */
import { SG, K, gd, spMul } from '../../core/kit.ts';
import type { Unit } from '../types.ts';
import { defineHero, hook, P } from '../define.ts';

export default defineHero({
  id: 'chengyu', name: '程昱', sub: '字仲德', faction: 'wei', role: '法師/輔助', weapon: 'staff',
  look: { head: 'guan', robe: true, beard: 'long', eye: 'fierce', fx: '#c8a0ff', c: { cloth: '#3a3a5a', sleeve: '#3a3a5a', pole: '#2a2030' } },
  skills: [
    ['伏兵', 'thunder', '指定地點 1 秒後竄出伏兵，範圍 {92/150/207/265} 傷害並擊飛 0.75 秒。', { x: { k: 'aoe', dmg: [92, 150, 207, 265], r: 120, delay: 1, cc: { knockup: 0.75 }, fx: 'slam', ground: true }, aim: P(650, 120), cd: 11, mp: [70, 80, 90, 100], el: null }],
    ['斷糧', 'trap', '指定區域 6 秒：區域內敵人受到的治療與回復 -{30/40/50/60}%、氣不會回復；敵方糧倉在區域內時多受 50% 傷害。', { x: { k: 'famine', r: 260, dur: 6, cut: [30, 40, 50, 60] }, aim: P(600, 260), cd: [22, 20, 18, 16], mp: 90, el: 'poison' }],
    ['剛戾', 'passive', '600 內每有一名敵將，防禦 +{2/3/4/5}。', { pv: {}, sig: 'stern', armor: [2, 3, 4, 5] }],
    ['十面埋伏', 'command', '在大範圍外圈立起十面軍旗 6 秒，外圈是牢籠：敵將碰到外圈會撞上伏兵，暈眩 {1.5/2/2.5} 秒並受到 {150/225/300} 傷害；2 秒後十隊伏兵從旗下朝中心衝鋒，各 {60/90/120} 傷害。', { x: { k: 'ambush10', R: 380, dur: 6, dmg: [150, 225, 300], stun: [1.5, 2, 2.5], charge: [60, 90, 120] }, aim: P(700, 380), cd: [120, 105, 90], mp: [150, 175, 200], el: 'shadow', shout: '十面埋伏，袁軍休走！' }],
  ],
  // 專屬技能（技能資料裡的 x.k）
  kinds: {
    famine(u, a, X) {
      this.zone(u, { x: a.pt[0], y: a.pt[1], r: X.r, t: X.dur, tick: 9, ground: true, pal: 'poison', name: a.sk.name, x2: { famine: X.cut } });
    },
    ambush10(u, a, X) {
      const c = a.pt, sp = spMul(u);
      this.zone(u, { x: c[0], y: c[1], r: X.R, t: X.dur, dur0: X.dur, tick: 9, name: a.sk.name, x2: { cage: { R: X.R, dmg: X.dmg * sp, stun: X.stun, hit: {} } } });
      this.later(2, () => {
        for (let k = 0; k < 10; k++) {
          const ang = (k / 10) * Math.PI * 2, sx = c[0] + Math.cos(ang) * X.R, sy = c[1] + Math.sin(ang) * X.R * K;
          const dir = [-Math.cos(ang), -Math.sin(ang)];
          const p = this.shootProj(u, { dir, speed: 620, range: X.R, rad: 36, shape: 'cavalry', foot: true, el: 'shadow', filter: (t: Unit) => !t.structure,
            onHit: (t: Unit) => { this.damage(u, t, X.charge * sp, { magic: true, spell: true, stagger: 0.3 }); return false; } });
          p.x = sx; p.y = sy;
        }
      });
    },
  },
  // 被動與專屬效果：hook(時間點, 順位, 處理)（時間點一覽見 core/hooks.ts）
  hooks: [
    // 斷糧：糧倉在區域內多受 50% 傷害
    hook('damage.before', 150, function (c) { const t = c.t; if (t.kind === 'granary' && this.zones.some((z) => z.x2 && z.x2.famine && z.team !== t.team && gd(z.x, z.y, t.x, t.y) <= z.r + t.r)) c.a *= 1.5; }),
    // 斷糧：治療減少
    hook('heal.mul', 10, function (t, v) { if (t.famT > this.t) v *= 1 - t.famCut / 100; return v; }),
    // 斷糧：區域內的敵人抵掉自然回復
    hook('zone', 20, function (z, dt) {
      const e = z.x2;
      if (e.famine) for (const t of this.units) {
        if (!t.alive || t.team === z.team || t.team > 1 || t.structure || gd(z.x, z.y, t.x, t.y) > z.r) continue;
        t.famT = this.t + 0.2; t.famCut = e.famine;
        if (t.kind === 'hero') { if (!SG.isNeng(t)) t.mp = Math.max(0, t.mp - t.maxMp * 0.012 * dt); t.hp = Math.max(1, t.hp - t.maxHp * 0.004 * dt * e.famine / 100); }
      }
    }),
    // 十面埋伏：撞到圈邊的敵將受傷、暈眩並被推回圈內
    hook('zone', 40, function (z, dt) {
      const e = z.x2;
      if (e.cage) for (const t of this.units) {
        if (!t.alive || t.kind !== 'hero' || t.team === z.team || e.cage.hit[t.id]) continue;
        const d = gd(z.x, z.y, t.x, t.y);
        if (d < e.cage.R - 28 || d > e.cage.R + 36) continue;
        e.cage.hit[t.id] = 1;
        this.damage(z.owner, t, e.cage.dmg, { magic: true, spell: true, stagger: 0.4 });
        this.applyCC(z.owner, t, { stun: e.cage.stun });
        const dir = SG.dirTo(t, z.x, z.y); SG.move(t, dir[0] * 50, dir[1] * 50);
        this.fx({ k: 'slam', x: t.x, y: t.y, col: '#b07cff' });
      }
    }),
    // 剛戾：附近每有一名敵將加護甲
    hook('tick', 10, function (u, dt) {
      const g = u.sg;
      if (g && g.stern && (u.sternT = (u.sternT || 0) - dt) <= 0) {
        u.sternT = 0.5;
        const n = this.enemiesNear(u, u.x, u.y, 600).filter((e) => e.kind === 'hero').length;
        this.addBuff(u, { id: 'stern', t: 0.8, armor: n * g.stern.v('armor') });
      }
    }),
  ],
  // 電腦什麼時候放這招
  ai: {
    famine: ({ w, u, t, d, hero, hpR, foeHeroes }) => hero && d < 600,
    ambush10: ({ w, u, t, d, hero, hpR, foeHeroes }) => hero && d < 650,
  },
});
